import { useContext } from "react";
import { useTranslation, Trans } from "react-i18next";

import { Button, Tag } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { PageTitle, Regular, Semibold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { MemberCardFront, MemberCardBack } from "@/App/components";
import { BlankPageWrapper } from "@/App/components/BlankPageWrapper";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { SearchParamKeys } from "@/App/constants/constants";
import { ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { useIsUnifiedUser, useNavigateClientNewTab } from "@/App/hooks";
import { getCustomerSupportNumber } from "@/App/utils/utils";

export const MemberIDCard = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClientNewTab();
  const { isUnifiedUser } = useIsUnifiedUser();
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { customerSupportPhoneNumber } = useContext(ExperienceContext);
  const customerSupportNumber = getCustomerSupportNumber(memberEligibility, customerSupportPhoneNumber);
  const onPrintCardPressed = () => {
    if (isViewingDataholder) {
      const newParams = new URLSearchParams();
      newParams.append(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID, memberEligibility.member.id.toString());
      navigate("/id_card", newParams);
    } else {
      navigate("/id_card");
    }
  };

  return (
    <BlankPageWrapper>
      <Stack testId="member-card-container" spacing="large">
        <Stack spacing="small">
          <PageTitle>{translate("MEMBER_ID_CARD_PAGE.title")}</PageTitle>
          <Regular>{translate("MEMBER_ID_CARD_PAGE.replacementCard")} <a href={`tel: ${customerSupportNumber}`}>{customerSupportNumber}.</a></Regular>
        </Stack>
        <Stack>
          <Stack spacing="large">
            <StackOrShelf alignment="center" spacing="large" isTabletNarrow>
              <MemberCardFront />
              {isUnifiedUser && (
                <MemberCardBack />
              )}
            </StackOrShelf>
            <Shelf alignment="center">
              <Button icon="print" testId="print-card" appearance="secondary" onClick={onPrintCardPressed}>
                {translate("DASHBOARD.planDetailsPanel.printCard")}
              </Button>
            </Shelf>
          </Stack>
        </Stack>
        {
          isUnifiedUser && (
            <>
              <Stack spacing="small">
                <Subheading>{translate("MEMBER_ID_CARD_PAGE.preCertification")}</Subheading>
                <Regular>{translate("MEMBER_ID_CARD_PAGE.callJudiHealth")}<Semibold>1-833-599-1020</Semibold><br/>
                <Trans
                  i18nKey="MEMBER_ID_CARD_PAGE.preCertificationMessage"
                  components={{ br: <br /> }}
                /></Regular>
              </Stack>
              <Stack spacing="medium">
                <Stack spacing="small">
                  <Subheading>{translate("MEMBER_ID_CARD_PAGE.memberClaims")}</Subheading>
                  <Stack spacing="none">
                    <Regular>
                      <Semibold>Cigna Medical Claims</Semibold>
                    </Regular>
                    <Regular>PO Box 188061</Regular>
                    <Regular>Chattanooga, TN 37422 - 8061</Regular>
                    <br />
                    <Regular><Semibold>{translate("MEMBER_ID_CARD_PAGE.electronicPayerId")}:</Semibold> 62308</Regular>
                    <Regular>{translate("MEMBER_ID_CARD_PAGE.cignaWebsiteMessage")} <a href="https://www.mycigna.com">www.myCigna.com</a></Regular>
                    <br />
                    <Regular>*{translate("MEMBER_ID_CARD_PAGE.cignaBenefitsDisclosure")}</Regular>
                  </Stack>
                </Stack>
                <div><Tag type="system-high-contrast">Away from Home Care</Tag></div>
              </Stack>
              <Stack>
                <Stack spacing="small">
                  <Subheading>{translate("MEMBER_ID_CARD_PAGE.rxClaims")}</Subheading>
                  <Stack spacing="none">
                    <Regular>
                      <Semibold>Capital Rx</Semibold>
                    </Regular>
                    <Regular>Attn: Claims</Regular>
                    <Regular>9450 SW Gemini Dr. #87234</Regular>
                    <Regular>Beaverton, Oregon 97008-7105</Regular>
                    <br />
                    <Semibold>{translate("MEMBER_ID_CARD_PAGE.memberPharmacyHelpLine")}:</Semibold>
                    <Regular>1-833-599-1020</Regular>
                  </Stack>
                </Stack>
              </Stack>
            </>
          )
        }
      </Stack>
    </BlankPageWrapper>
  );
};
